import { type ReactNode } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { LocaleSwitcher } from '@/components/locale-switcher';
import { ThemeSwitcher } from '@/components/theme-switcher';
import { BrandLogo } from '@/components/brand-logo';
import { hasPermission, useAuthStore } from '@/auth/auth-store';
import { cn } from '@/lib/utils';
import { platformVersionLabel } from '@/lib/build-info';
import { securescaleNavEntry } from '@securescale/admin-pages';
import { useT } from '@/i18n/use-t';

type NavEntry = {
  to: string;
  label: string;
  labelKey?: string;
  superAdmin?: boolean;
  /**
   * The feature module this page belongs to, as registered in `ModuleRegistry`.
   *
   * A tenant not licensed for the module never sees the entry. Before RL-377 the
   * admin UI had no way to know its own entitlements, so every entry showed to
   * everyone and an unlicensed one answered 404 on click — `Kitchen orders` did
   * exactly that for every tenant but Fayno.
   *
   * Entries with no module are core surfaces (roles, users, i18n, integrations):
   * always present, gated by permission rather than licence.
   */
  module?: string;
  /**
   * A permission the user must hold to see the entry at all (super-admins
   * always do). For sections whose every screen needs it — the Fiscal cabinet
   * (PFC-9) is empty for anyone without `fiscal.cabinet.read`.
   */
  permission?: string;
  /**
   * SSC-95: an ABSENT module list hides the entry instead of showing it.
   * The default fails open (see `AppShell`); a sidebar entry for a module
   * the server may not even know about fails closed.
   */
  failClosed?: boolean;
  /**
   * SSC-95: the user must hold every one of these. Super-admins hold all,
   * as in `useHasPermission`. A licence is still required on top.
   */
  permissions?: readonly string[];
  /** At least one workflow permission; licence still required. */
  anyPermissions?: readonly string[];
};

// Exported so routes.test.ts (RFL-50) can assert every sidebar entry
// maps to a real route in the canonical inventory.
export const NAV: NavEntry[] = [
  {
    to: '/sanitex/item-diagnosis',
    label: 'Sanitex item diagnosis',
    labelKey: 'sanitex.diagnosis.title',
    module: 'sanitex',
    permission: 'sanitex.read',
  },
  {
    to: '/sanitex/pending-card-authorisations',
    label: 'Pending card authorisations',
    labelKey: 'sanitex.pendingCards.title',
    module: 'sanitex',
    permission: 'payments.read',
  },
  { to: '/', label: 'Dashboard', labelKey: 'nav.dashboard' },
  { to: '/catalog/items', label: 'Catalog', labelKey: 'nav.catalog', module: 'catalog' },
  { to: '/catalog/categories', label: 'Categories', labelKey: 'nav.categories', module: 'catalog' },
  {
    to: '/pricing/price-lists',
    label: 'Price lists',
    labelKey: 'nav.priceLists',
    module: 'pricing',
  },
  {
    to: '/import-export/csv',
    label: 'CSV import',
    labelKey: 'nav.csvImport',
    module: 'import-export',
  },
  { to: '/locations', label: 'Locations', labelKey: 'nav.locations', module: 'locations' },
  { to: '/provision', label: 'Provision', labelKey: 'nav.provision', module: 'provision' },
  {
    to: '/terminals',
    label: 'Terminals',
    labelKey: 'nav.terminals',
    module: 'terminal-management',
  },
  {
    to: '/terminal-profiles',
    label: 'Terminal profiles',
    labelKey: 'nav.terminalProfiles',
    module: 'terminal-management',
  },
  {
    to: '/terminal-logs',
    label: 'Terminal logs',
    labelKey: 'nav.terminalLogs',
    module: 'terminal-management',
  },
  {
    to: '/terminals/kitchen-orders',
    label: 'Kitchen orders',
    labelKey: 'nav.kitchenOrders',
    module: 'kitchen',
  },
  {
    to: '/terminal-flows',
    label: 'Flow rules',
    labelKey: 'nav.flowRules',
    module: 'terminal-flows',
  },
  {
    to: '/screen-flows',
    label: 'Screen flows',
    labelKey: 'nav.screenFlows',
    module: 'screen-flows',
  },
  {
    to: '/country-packs',
    label: 'Country packs',
    labelKey: 'nav.countryPacks',
    module: 'country-packs',
  },
  { to: '/catalog-hotkeys', label: 'Hotkeys', labelKey: 'nav.hotkeys', module: 'catalog-hotkeys' },
  { to: '/parameters', label: 'Parameters', labelKey: 'nav.parameters', module: 'terminal-params' },
  { to: '/ui-themes', label: 'UI themes', labelKey: 'themes.title', module: 'ui-themes' },
  { to: '/employees', label: 'Cashiers', labelKey: 'nav.cashiers', module: 'employees' },
  { to: '/shifts', label: 'Shifts', labelKey: 'nav.shifts', module: 'shifts' },
  {
    to: '/cash-movements',
    label: 'Cash movements',
    labelKey: 'nav.cashMovements',
    module: 'shifts',
  },
  { to: '/reports/turnover', label: 'Turnover', labelKey: 'nav.turnover', module: 'shifts' },
  {
    to: '/reports/returns',
    label: 'Returns',
    labelKey: 'nav.returns',
    module: 'receipts',
    permission: 'receipts.read',
  },
  {
    to: '/reports/promotion-discounts',
    label: 'Promotion discounts',
    labelKey: 'nav.promotionDiscounts',
    module: 'receipts',
    permission: 'receipts.read',
  },
  {
    to: '/payments/reconciliation',
    label: 'Payments queue',
    labelKey: 'nav.paymentsQueue',
    module: 'payments',
  },
  {
    to: '/weight/coverage',
    label: 'Weight coverage',
    labelKey: 'nav.weightCoverage',
    module: 'weight',
  },
  // SSC-95: SecureScale's own descriptor — module `securescale`, permission
  // `weight.manage` (`weight.view` arrives with SSC-106). Fails closed.
  { ...securescaleNavEntry, failClosed: true },
  {
    to: '/securescale/storage-config',
    label: 'Resource storage quotas',
    labelKey: 'securescale.quota.title',
    module: 'securescale',
    permissions: ['terminal-params.write'],
    failClosed: true,
  },
  {
    to: '/securescale/investigations',
    label: 'Investigations',
    labelKey: 'securescale.investigations.title',
    module: 'securescale',
    failClosed: true,
    anyPermissions: ['weight.investigate', 'weight.approve'],
  },
  { to: '/approvals', label: 'Approvals', labelKey: 'nav.approvals', module: 'approvals' },
  { to: '/receipts', label: 'Receipts', labelKey: 'nav.receipts', module: 'receipts' },
  {
    to: '/receipts/legacy-import',
    label: 'Legacy receipt import',
    labelKey: 'nav.legacyReceiptImport',
    module: 'receipts',
  },
  { to: '/promotions', label: 'Promotions', labelKey: 'nav.promotions', module: 'promotions' },
  {
    to: '/fiscal-cabinet',
    label: 'Fiscal cabinet',
    labelKey: 'nav.fiscalCabinet',
    module: 'fiscal-cabinet',
    permission: 'fiscal.cabinet.read',
  },
  {
    to: '/certificates',
    label: 'Certificates',
    labelKey: 'nav.certificates',
    module: 'certificates',
  },
  {
    to: '/receipt-config/editor',
    label: 'Receipt config',
    labelKey: 'nav.receiptConfig',
    module: 'receipt-config',
  },
  { to: '/api-docs', label: 'API reference', labelKey: 'nav.apiDocs' },
  { to: '/i18n/strings', label: 'i18n strings', labelKey: 'nav.i18nStrings' },
  {
    to: '/settings/dashboard-widgets',
    label: 'Dashboard widgets',
    labelKey: 'nav.dashboardWidgets',
    module: 'dashboard',
  },
  { to: '/roles', label: 'Roles', labelKey: 'nav.roles' },
  { to: '/users', label: 'Users', labelKey: 'nav.users', superAdmin: true },
  {
    to: '/settings/integrations',
    label: 'Integrations',
    labelKey: 'nav.integrations',
    superAdmin: true,
  },
];

export function AppShell({ children }: { children: ReactNode }) {
  // Entitlement gating for the sidebar. `modules` is undefined against a server
  // that predates RL-377 and for a session persisted before it, so an absent
  // list means "assume everything" — the same fail-open rule `useHasModule`
  // documents. Hiding pages a tenant has paid for is the worse failure, and the
  // guard, not the sidebar, is what actually withholds an unlicensed module.
  //
  // An entry marked `failClosed` (SSC-95, SecureScale) reads an absent list as
  // "not licensed" instead: it is a separate product, and showing it to a
  // tenant that has not bought it is the worse failure there.
  const modules = useAuthStore((s) => s.user?.modules);
  const licensed = (entry: NavEntry): boolean =>
    entry.module === undefined ||
    (modules === undefined ? entry.failClosed !== true : modules.includes(entry.module));

  const user = useAuthStore((s) => s.user);
  const permitted = (entry: NavEntry): boolean =>
    user?.isSuperAdmin === true ||
    ((entry.permission === undefined || hasPermission(user, entry.permission)) &&
      (entry.permissions === undefined ||
        entry.permissions.every((code) => user?.permissions?.includes(code) === true)) &&
      (entry.anyPermissions === undefined ||
        entry.anyPermissions.some((code) => user?.permissions?.includes(code) === true)));

  const visibleNav = NAV.filter(
    (entry) =>
      (entry.superAdmin !== true || user?.isSuperAdmin === true) &&
      licensed(entry) &&
      permitted(entry),
  );
  const logout = useAuthStore((s) => s.logout);
  const location = useLocation();
  const t = useT('admin_ui');

  return (
    <div className="grid min-h-screen grid-cols-[240px_1fr] bg-background text-foreground">
      <aside className="border-r border-border bg-secondary/40">
        {/* Coral leading bar — the brand cue (design-doc §3). */}
        <div className="flex items-stretch">
          <div className="reform-coral-bar" />
          <Link to="/" className="block px-4 py-4">
            <BrandLogo height={32} />
          </Link>
        </div>
        <nav className="space-y-0.5 px-2 pb-4 pt-2">
          {visibleNav.map((entry) => {
            const active =
              entry.to === '/' ? location.pathname === '/' : location.pathname.startsWith(entry.to);
            return (
              <NavLink
                key={entry.to}
                to={entry.to}
                className={cn(
                  'block rounded-md px-3 py-2 text-sm transition-colors',
                  active
                    ? 'bg-card font-medium text-primary'
                    : 'text-muted-foreground hover:bg-secondary',
                )}
              >
                {entry.labelKey === undefined ? entry.label : t(entry.labelKey, entry.label)}
              </NavLink>
            );
          })}
        </nav>
        {/* Which platform build this is — the `platform/<x.y.z>` tag it was deployed from. */}
        <p
          className="px-5 pb-4 text-xs text-muted-foreground/70"
          data-testid="platform-version"
          title={t('shell.platformVersion', 'Platform version')}
        >
          {platformVersionLabel}
        </p>
      </aside>

      <div className="flex min-w-0 flex-col">
        <header className="flex items-center justify-end gap-3 border-b border-border bg-card px-6 py-3">
          <ThemeSwitcher />
          <LocaleSwitcher />
          {user !== null && <span className="text-sm text-muted-foreground">{user.email}</span>}
          <Button variant="outline" size="sm" onClick={() => logout()}>
            {t('shell.signOut', 'Sign out')}
          </Button>
        </header>
        <main className="min-w-0 flex-1 overflow-auto px-6 py-6">{children}</main>
      </div>
    </div>
  );
}
